<template>
  <div class="app-checkout-payment-data">
    <SimonModal
      :is-visible="showSEPAInfoModal"
      @hide="showSEPAInfoModal = false"
      id="sepa-info-modal"
    >
      <slot name="txtConfirmationInfo"></slot>
    </SimonModal>
    <div class="payment-data--header"><slot name="txtHeadline">Lorem ipsum dolor sit amet.</slot></div>
    <div class="payment-data--form">
      <b-form id="payment-data-form" :novalidate="true">
        <div class="form--block">
          <h2 class="h3"><slot name="lblLastschriftHeadline">Lorem ipsum.</slot></h2>
          <div class="form-row">
          <LabeledTextInput
            name="iban"
            :model.sync="bankAccount.iban"
            :invalid.sync="invalid.iban"
            :validation="validations.iban"
            :formatter="ibanFormatter"
            :mandatory="true"
            ref="nestedInputIban"
          >
            <template v-for="slot in possibleChildSlotsFor('Iban')" v-slot:[`${slot.child}`]><slot :name="`${slot.parent}`"></slot></template>
            <template v-for="slotError in possibleChildSlotsFor('IbanError')" v-slot:[`${slotError.child}`]><slot :name="`${slotError.parent}`"></slot></template>
          </LabeledTextInput>
          </div>
          <div class="form-row">
          <LabeledTextInput
            name="accountHolder"
            :model.sync="bankAccount.accountHolder"
            :invalid.sync="invalid.accountHolder"
            :validation="validations.accountHolder"
            disabled
            :formatter="formatterCharFilterOnlyAplha"
            ref="nestedInputAccountHolder"
          >
            <template v-for="slot in possibleChildSlotsFor('AccountHolder')" v-slot:[`${slot.child}`]><slot :name="`${slot.parent}`"></slot></template>
            <template v-for="slotError in possibleChildSlotsFor('Error')" v-slot:[`${slotError.child}`]><slot :name="`${slotError.parent}`"></slot></template>
            <template slot="txtInfoModal"><slot name="txtAccountHolder"/></template>
          </LabeledTextInput>
          </div>

          <div class="card card--info">
            <LabeledCheckbox
              name="PaymentConfirmation"
              :model.sync="bankAccount.confirmation"
              :invalid.sync="invalid.confirmation"
              :mandatory="true"
              ref="nestedInputConfirmation"
            >
              <template slot="txt">
                <p><slot name="txtPreConfirmation"></slot> <a href="javascript:;" @click="showSEPAInfoModal = true">
                  <slot name="txtConfirmation"></slot></a></p>
              </template>
              <template v-for="slotError in possibleChildSlotsFor('Error')" v-slot:[`${slotError.child}`]><slot :name="`${slotError.parent}`"></slot></template>
            </LabeledCheckbox>
          </div>
          <div class="policy"><slot name="txtPaymentInfo"></slot></div>
        </div>
      </b-form>
    </div>
    <Stepper
      :loading-next="loading"
      :show-prev="true"
      @prev="$emit('goBack')"
      @next="save()"
    >
      <template v-for="slot in possibleChildSlotsFor('Stepper')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </Stepper>
  </div>
</template>

<script>
import Stepper from '@/components/generic/Stepper.vue'
import LabeledTextInput from '@/components/forms/LabeledTextInput.vue'
import { maxLength, required } from 'vuelidate/lib/validators'
import LabeledCheckbox from '@/components/forms/LabeledCheckbox.vue'
import validationFlow from '@/mixins/validators/validationFlow'
import formatterCharFilterOnlyAplha from '@/mixins/formatters/inputFormatter'
import ibanValidator from '@/mixins/validators/ibanValidator'
import ibanFormatter from '@/mixins/formatters/ibanFormatter'
import SimonModal from '@/components/modal/SimonModal.vue'
import trackingHandler from '@/mixins/trackingHandler'

export default {
  name: 'PaymentData',
  components: { SimonModal, LabeledCheckbox, LabeledTextInput, Stepper },
  props: {
    process: {
      type: String,
      default: 'ordering'
    }
  },
  mixins: [
    validationFlow,
    formatterCharFilterOnlyAplha,
    trackingHandler,
    ibanValidator,
    ibanFormatter
  ],
  data () {
    return {
      loading: false,
      showSEPAInfoModal: false,
      bankAccount: { iban: '', accountHolder: '', confirmation: false },
      invalid: {
        iban: null,
        accountHolder: null,
        confirmation: null
      },
      validations: {
        iban: {
          required,
          iban: (v) => {
            return this.validateIBAN(v)
          }
        },
        accountHolder: {
          required,
          maxLength: maxLength(50)
        }
      }
    }
  },
  created () {
    if (this.getPerson && this.getPerson.firstName) {
      this.bankAccount.accountHolder = (this.getPerson.firstName + ' ' + this.getPerson.lastName).substr(0, 50)
    }
    this.bankAccount.iban = this.getBankAccount.iban
    this.bankAccount.confirmation = this.getBankAccount.confirmation
  },
  computed: {
    formInvalid: function () {
      return Object.entries(this.invalid)
        .map(([key, value]) => value)
        .some((value) => value === true)
    },
    getPerson () {
      return this.$store.getters['checkoutv2/' + this.process + '/_customerData/person']
    },
    getAddress () {
      return this.$store.getters['checkoutv2/' + this.process + '/_customerData/address']
    },
    getBankAccount () {
      return this.$store.getters['checkoutv2/' + this.process + '/_paymentData/bankAccount']
    },
    getCurrentStepName: function () {
      return this.$store.getters['checkoutv2/' + this.process + '/step']
    }
  },
  methods: {
    forceValidationOnNestedInputs () {
      Object.entries(this.$refs).forEach(([key, comp]) => {
        if (/^nestedInput/.test(key) && comp !== undefined) {
          comp.vflowForceFeedback()
        }
      })
    },
    findAndSetFocusManually () {
      const targetStepName = this.getCurrentStepName
      const targetStepLi = document.querySelector(`li.${targetStepName}`)

      if (targetStepLi) {
        // Find the button
        const targetButton = targetStepLi.querySelector("[role='button']")

        if (targetButton) {
          targetButton.setAttribute('tabindex', '0') // make it focusable
          targetButton.focus()
        }
      }
    },
    save () {
      this.forceValidationOnNestedInputs()

      if (!this.formInvalid) {
        this.startLoader()
        this.loading = true

        this.$store
          .dispatch('checkoutv2/' + this.process + '/_paymentData/validateAndSavePayment', {
            person: this.getPerson,
            address: this.getAddress,
            bankAccount: this.bankAccount,
            process: this.process
          })
          .then(() => {
            return this.process === 'ordering'
              ? this.$store.dispatch('checkoutv2/' + this.process + '/initiate')
              : true
          })
          .then(() => {
            this.clearToasts()
            this.$emit('goForward')
          })
          .catch((error) => {
            this.errorToast(this.getErrorMessage(error))
            this.triggerErrorTracking(error)
            throw new Error('validateAndSavePayment failed')
          })
          .finally(() => {
            this.loading = false
            this.stopLoader(() => {
              this.findAndSetFocusManually()
            })
          })
      } else {
        this.vFlowScrollToFirstElemment()
        this.vFlowGetFirstErrorElementName().then((result) => {
          this.triggerValidationErrorTracking(result)
        })
      }
    }
  }
}
</script>
